import React from 'react'
import PropTypes from 'prop-types'
import { InputNumber } from 'antd'
import { compose, equals, complement, isNil } from 'ramda'
import { classNames, isNumeric } from 'utils/utils'
import strings from 'utils/strings'
import InputLabel from 'components/patterns/Inputs/InputLabel'
import withStyle from '../Input.style.js'

const notEquals = complement(equals)

class NumberInput extends React.Component {
  constructor(props) {
    super(props)
    const validation = this.validateValue(props.value) || {}
    this.state = {
      value: props.value,
      prevValue: props.value,
      ...validation,
    }
  }

  valueRequiredPresent = (val) => {
    if (!this.props.required) return false
    return isNil(val)
  }

  valueOutsideOfRange = (val) => {
    if (isNil(val)) {
      return false
    }
    const { min, max } = this.props
    const aboveMin = min ? val >= min : true
    const belowMax = max ? val <= max : true
    return !aboveMin || !belowMax
  }

  validateValue = (val) => {
    const { customErrorMessage } = this.props
    const requiredValueError = this.valueRequiredPresent(val)
    const rangeError = this.valueOutsideOfRange(val)
    const isValid = !requiredValueError && !rangeError && !customErrorMessage

    return {
      requiredValueError,
      rangeError,
      isValid,
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const { value, defaultValue, updateOnDifferentValue } = nextProps
    const { prevValue } = prevState

    if (notEquals(value, prevValue)) {
      if (equals(value, defaultValue)) {
        return {
          ...prevState,
          value,
          prevValue: value,
          isValid: true,
          hasBlurred: false,
        }
      }
      if (updateOnDifferentValue) {
        return {
          ...prevState,
          value,
          prevValue: value,
        }
      }
      return {
        ...prevState,
        prevValue: value,
      }
    }

    return null
  }

  componentDidUpdate(prevProps, prevState) {
    const { value, onValidityChange, name, paramName, customErrorMessage } =
      this.props
    const {
      rangeError: prevRangeError,
      requiredValueError: prevRequiredValueError,
      isValid: wasValid,
    } = prevState
    if (prevProps.value !== value) {
      const validation = this.validateValue(value)
      const { isValid, requiredValueError, rangeError } = validation
      if (isValid !== wasValid && onValidityChange) {
        onValidityChange(
          isValid,
          paramName || name,
          this.getFooterText({
            /* eslint-disable no-bitwise */
            rangeError: rangeError ^ prevRangeError,
            requiredValueError: requiredValueError ^ prevRequiredValueError,
            errorMessage: customErrorMessage || prevProps.customErrorMessage,
          })
        )
      }
      this.setState(validation)
    }
  }

  handleChange = (value) => {
    if (!isNumeric(value)) return null
    const { onChange = () => {} } = this.props
    onChange(value)
    this.setState({ value })
  }

  handleBlur = (event) => {
    const { onBlur = () => {} } = this.props
    onBlur(event)
  }

  handleFocus = (event) => {
    const { onFocus = () => {} } = this.props
    onFocus(event)
  }

  getFooterText = ({ rangeError, requiredValueError, errorMessage }) => {
    const { name, min, max, paramName } = this.props
    if (requiredValueError) return strings.required
    if (errorMessage) return errorMessage
    if (rangeError)
      return strings.numeric_range_error(paramName || name, min, max)
    return null
  }

  render() {
    const {
      disabled,
      name,
      className,
      inputClassName,
      label,
      customErrorMessage,
      box,
      vertical,
      max,
      min,
      step,
      noFooter,
      hint,
      tooltip,
      required,
      size,
    } = this.props
    const { isValid, rangeError, requiredValueError, value } = this.state

    const footerText = this.getFooterText({
      rangeError,
      requiredValueError,
      errorMessage: customErrorMessage,
    })

    return (
      <div
        className={classNames(className, 'form-field', 'input-box', {
          box,
          'has-error': !isValid || customErrorMessage,
        })}
      >
        {label && (
          <InputLabel
            label={label}
            hint={hint}
            tooltip={tooltip}
            required={required}
            size={size}
            vertical={vertical}
          />
        )}
        <InputNumber
          value={value}
          disabled={disabled}
          name={name}
          min={min}
          max={max}
          step={step}
          type="number"
          className={classNames(inputClassName)}
          onChange={this.handleChange}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          autoComplete="off"
        />
        {!noFooter && footerText && (
          <span className="input-footer">{footerText}</span>
        )}
      </div>
    )
  }
}

NumberInput.defaultProps = {
  onChange: undefined,
  name: 'InputNumber',
  disabled: false,
  required: false,
  step: 1,
  size: 'default',
  defaultValue: 0,
}

NumberInput.propTypes = {
  /** number value being edited */
  value: PropTypes.number,
  /** boolean value determines whether or not the component is disabled */
  disabled: PropTypes.bool,
  /** boolean value describing whether the value is required or not */
  required: PropTypes.bool,
  /** maximum allowable value */
  max: PropTypes.number,
  /** minimum allowable value */
  min: PropTypes.number,
  /** single step increment applied to value when user changes val using buttons */
  step: PropTypes.number,
  /** callback fired when the the user focuses on the component */
  onFocus: PropTypes.func,
  /** callback fired when the component loses focus */
  onBlur: PropTypes.func,
  /** callback fired when the user updates the value */
  onChange: PropTypes.func,
  /** callback fired when value validity changes */
  onValidityChange: PropTypes.func,
  /** Don't know what this is for */
  updateOnDifferentValue: PropTypes.bool,
  /** name value given to component and synthetic events fired within the component */
  name: PropTypes.string,
  /** name value displayed to user along with errors/user feedback */
  paramName: PropTypes.string,
  /** string value provided when an external error related to component value is generated */
  customErrorMessage: PropTypes.string,
  /** string css classname for further augmenting styles of the component wrapper */
  className: PropTypes.string,
  /** string css classname for further augmenting styles of the input element */
  inputClassName: PropTypes.string,
  /** string label displayed to the user above the component */
  label: PropTypes.string,
  /** boolean value determining box display behavior */
  box: PropTypes.bool,
  /** boolean value determining vertical display behavior */
  vertical: PropTypes.bool,
  /** text or icon embedded in the left side of the element */
  size: PropTypes.oneOf(['small', 'default', 'large']),
  /** width value in pixels of the input element */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** boolean to permanently hide footer if, for example, errors are displayed elsewhere in context */
  noFooter: PropTypes.bool,
  /** optional string next to label */
  hint: PropTypes.string,
  /** optional string to render tooltip next to label */
  tooltip: PropTypes.string,
  /** the default value to reset force reset */
  defaultValue: PropTypes.number,
}

export default compose(withStyle)(NumberInput)
